import { useEffect, useCallback, ReactNode } from 'react';
import { useBlocker } from 'react-router-dom';
import { Modal, SpaceBetween, Button } from '@cloudscape-design/components';

interface UnsavedChangesGuardProps {
  isDirty: boolean;
  children: ReactNode;
}

/**
 * Guards against accidental navigation when unsaved changes exist.
 * - Browser close/refresh: uses beforeunload event (native browser dialog)
 * - In-app navigation: uses react-router-dom useBlocker (custom Cloudscape modal)
 *
 * Requirements: 12.1, 12.2, 12.3, 12.4, 12.5, 12.6, 12.7
 */
export function UnsavedChangesGuard({ isDirty, children }: UnsavedChangesGuardProps) {
  // Browser close/refresh warning
  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // Required for Chrome
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // In-app navigation blocking
  const blocker = useBlocker(isDirty);

  const handleProceed = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker]);

  const handleStay = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  return (
    <>
      {children}
      <Modal
        visible={blocker.state === 'blocked'}
        onDismiss={handleStay}
        header="Unsaved changes"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleStay}>Stay on page</Button>
            <Button variant="primary" onClick={handleProceed}>
              Leave anyway
            </Button>
          </SpaceBetween>
        }
      >
        You have unsaved changes. If you leave this page your changes will be lost.
      </Modal>
    </>
  );
}
